import { useTranslation } from "react-i18next";
import { IconTrash } from "@tabler/icons-react";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../../domains/user/hooks/useCurrentUser";
import { saveAvatarPick } from "../../../../domains/user/saveAvatarPick";
import { defaultAvatarImages } from "../../../../domains/user/utils/pickUserImage";
import {
  useAvatarPicker,
  setPickedAvatar,
  setRemovedAvatar,
  resetAvatarPicker,
} from "../../../../domains/user/stores/avatarPickerStore";
import { displayToast } from "../../../app/AppToast";
import { Button } from "../../../ui/Button";
import Loader from "../../../Loader";
import CustomAvatarUploader from "../../../profile/CustomAvatarUploader";
import { popSettingsView } from "../../../../domains/app/stores/settingsSheetStore";
import { SettingsSubHeader } from "../SettingsSubHeader";

export function EditAvatarView(): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const pick = useAvatarPicker();

  if (currentUser === null) {
    return (
      <div className="flex h-full flex-col">
        <SettingsSubHeader title={t("Edit avatar")} />
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      </div>
    );
  }

  const pic = currentUser.profilePicture;
  const customUrl =
    pic !== null && pic.image !== null && pic.image.url !== ""
      ? pic.image.url
      : "";
  const defaultUrl =
    pic !== null && pic.name !== null
      ? defaultAvatarImages[pic.name].default
      : "";

  // Show the freshly-picked photo, the empty Avatar placeholder once the
  // picture is dropped, else the user's current custom photo (or default one).
  const displayedUrl =
    pick.status === "picked"
      ? pick.dataUrl
      : pick.status === "removed"
        ? ""
        : customUrl !== ""
          ? customUrl
          : defaultUrl;

  const canRemove =
    pick.status === "picked" ||
    (pick.status === "untouched" && displayedUrl !== "");

  async function save(): Promise<void> {
    const result = await saveAvatarPick(pick);
    if (result === "untouched") return;
    if (result === "failed") {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    await reloadCurrentUser();
    displayToast(t("Profile picture updated"), "success");
    resetAvatarPicker();
    popSettingsView();
  }

  return (
    <div className="flex h-full flex-col">
      <SettingsSubHeader title={t("Edit avatar")} />
      <div className="flex-1 overflow-y-auto scroll-fog-y px-5 pt-4">
        <CustomAvatarUploader
          avatarUrl={displayedUrl}
          fullname={currentUser.fullname}
          buttonText={t("Upload profile picture")}
          onChange={(url) => {
            setPickedAvatar(url);
          }}
        />
        {canRemove && (
          <Button
            variant="ghost"
            size="lg"
            className="mt-3 w-full text-ds-strawberry-600"
            leftIcon={<IconTrash size={20} />}
            onClick={setRemovedAvatar}
          >
            {t("Remove photo")}
          </Button>
        )}
      </div>
      <div className="shrink-0 px-5 pt-2 pb-6">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={pick.status === "untouched"}
          onClick={() => {
            void save();
          }}
        >
          {t("Save")}
        </Button>
      </div>
    </div>
  );
}
